import { parseFt3dAoePlacementEnvelope } from '../shared/ft3d-aoe-placement-contract';
import { validateVfxRecipe } from '../shared/vfx-recipe-contract';
import {currentFt3dSceneDocument, currentFt3dSceneWindow, ft3dSceneReady, onFt3dSceneReady, postFt3dPresentation} from './scene-frame';
type Definition = {assets?:{magic_vfx?:unknown}};

/** Presentation only: use the page's compiled definition lookup and the scene's existing decoder. */
export function mountAreaVfxWarm(): void {
  const host=window as unknown as {ftFindFullNestedItem?:(id:string)=>unknown;
    FateTide?:{objectCache?:{get(id:string):unknown}}};
  let sceneDocument: Document | null = null, scheduled=false, rescan=false;
  const prepared=new Set<string>(),pending=new Map<string,{key:string;done:()=>void;timer:number}>();
  const current=():boolean=>{
    const currentDocument=currentFt3dSceneDocument();
    if(sceneDocument!==currentDocument){
      sceneDocument=currentDocument;prepared.clear();
      for(const p of pending.values()){window.clearTimeout(p.timer);p.done();}pending.clear();
    }
    return ft3dSceneReady();
  };
  const warm=(id:string):Promise<void>=>{
    if(!current())return Promise.resolve();
    const result=[()=>host.ftFindFullNestedItem?.(id),()=>host.FateTide?.objectCache?.get(id)].map(lookup=>{
      let definition:Definition|null=null;
      try{definition=lookup() as Definition|null;}catch{ /* The page lookup may still be mounting. */ }
      return validateVfxRecipe(definition?.assets?.magic_vfx,{});
    }).find(result=>result.kind==='valid'&&result.value.lifecycle!=='status');
    if(!result||result.kind!=='valid')return Promise.resolve();
    const key=JSON.stringify(result.value);
    if(prepared.has(key)||[...pending.values()].some(p=>p.key===key))return Promise.resolve();
    const requestId='area-warm:'+crypto.randomUUID();
    return new Promise<void>(done=>{
      const timer=window.setTimeout(()=>{pending.delete(requestId);done();},30000);
      pending.set(requestId,{key,done,timer});
      postFt3dPresentation({type:'ft3d-magic-effect-preload',requestId,recipes:[result.value]});
    });
  };
  const schedule=():void=>{
    current();
    if(scheduled){rescan=true;return;}scheduled=true;rescan=false;
    const run=async()=>{
      try{
        if(!current())return;
        const scannedDocument=sceneDocument;
        for(const button of document.querySelectorAll<HTMLElement>('[data-ft3d-actor-dock] .btn_ability_ranges[data-ability_id][data-aoe_type]')){
          if(currentFt3dSceneDocument()!==scannedDocument)break;
          if(button.dataset.aoe_type)await warm(String(button.dataset.ability_id??'').toUpperCase());
          // Yield between recipes; the armed recipe bypasses this idle queue.
          await new Promise<void>(resolve=>idle(resolve));
        }
      }finally{scheduled=false;if(rescan)schedule();}
    };
    idle(()=>{void run();});
  };
  const idle=(fn:()=>void):void=>{
    if(window.requestIdleCallback)window.requestIdleCallback(fn,{timeout:5000});else window.setTimeout(fn,250);
  };
  // Capture the ability click before any cast-level choice opens; placement intent also covers keyboard/programmatic arms.
  document.addEventListener('click',event=>{
    const button=(event.target as Element|null)?.closest<HTMLElement>('[data-ft3d-actor-dock] .btn_ability_ranges[data-ability_id][data-aoe_type]');
    if(button?.dataset.aoe_type)void warm(String(button.dataset.ability_id??'').toUpperCase());
  },true);
  window.addEventListener('ft:ft3d-aoe-placement-intent',event=>{
    const intent=parseFt3dAoePlacementEnvelope((event as CustomEvent).detail)?.intent;
    if(intent?.placementKind==='aoe')void warm(intent.abilityId);
  });
  window.addEventListener('message',event=>{
    if(event.origin!==location.origin||event.source!==currentFt3dSceneWindow())return;
    if(event.data?.type!=='ft3d-magic-effect-prepared')return;
    current();
    const entry=pending.get(event.data.requestId);if(!entry)return;
    pending.delete(event.data.requestId);window.clearTimeout(entry.timer);
    if(event.data.accepted===true)prepared.add(entry.key);entry.done();
  });
  onFt3dSceneReady(schedule);
  const dock=document.querySelector('[data-ft3d-actor-dock]');
  if(dock)new MutationObserver(schedule).observe(dock,{childList:true,subtree:true});
  schedule();
}
